import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/hooks/useAuth';
import { appsAPI } from '../api/appsAPI';
import SeoMeta from '../../../components/SeoMeta/SeoMeta';
import { isEduPortal } from '../../../config/eduHost';
import { appUrl } from '../../../config/appBase';
import LandingAppStoreFooter from './LandingAppStoreFooter';
import SkeletonLoader from './SkeletonLoader';
import HeroPromptWidget from './HeroPromptWidget';
import RoadblocksFeatureSection, { PromptMarquee } from './RoadblocksFeatureSection';
import { getTutorialIconLabel, isHiddenTaskListApp } from '../utils/appDisplayUtils';
import './AppStoreView.css';
import './LandingAppStore.css';
const heroSunsetImg = '/auth_cover.webp';

// Placeholder apps only when public API fails
const PLACEHOLDER_EXPERIENCE = [
  { id: 'cs', title: 'Computer Science', subject: 'Computer Science', images: { small: '/logo-yellow.png', medium: '/logo-yellow.png', large: '/logo-yellow.png' } },
  { id: 'med', title: 'Medicine', subject: 'Medicine', images: { small: '/logo-yellow.png', medium: '/logo-yellow.png', large: '/logo-yellow.png' } },
  { id: 'law', title: 'Law', subject: 'Law', images: { small: '/logo-yellow.png', medium: '/logo-yellow.png', large: '/logo-yellow.png' } },
  { id: 'ib', title: 'Investment Banking', subject: 'Investment Banking', images: { small: '/logo-yellow.png', medium: '/logo-yellow.png', large: '/logo-yellow.png' } },
];

const PLACEHOLDER_PRODUCTIVITY = [
  { id: 'tmua', exam_name: 'TMUA', title: 'TMUA', subject: 'Maths', app_type: 'productivity' },
  { id: 'ucat', exam_name: 'UCAT', title: 'UCAT', subject: 'Admissions', app_type: 'productivity' },
  { id: 'lnat', exam_name: 'LNAT', title: 'LNAT', subject: 'Law', app_type: 'productivity' },
];

const promoEvents = [
  {
    key: 'uai',
    badge: 'Ends on 07/31',
    headline: 'Plan your studies, organize tasks, and get instant feedback with uAI',
    description: 'Unleash your potential with our advanced AI tutor, personalized study planner, and instant case feedback.',
    appTitle: 'uAI Study Assistant',
    appSubtext: 'Ivori • AI Tools',
    defaultIcon: '/uai-icon.png',
    defaultBanner: '/api/apps/media/tutorials/uai/uai-1920x1080.png'
  },
  {
    key: 'code_lab',
    badge: 'Ends on 07/15',
    headline: 'Learn dynamic coding and run code in real-time',
    description: 'Practice Python, JS and SQL directly in your browser with interactive sandboxed code playgrounds.',
    appTitle: 'Code Lab',
    appSubtext: 'Ivori • Coding',
    defaultIcon: '/logo192.png',
    defaultBanner: '/api/apps/media/tutorials/code_lab/code_lab-1920x1080.png'
  },
  {
    key: 'computer_science_interview',
    badge: 'Ends on 06/30',
    headline: 'Prep for top tech companies with mock programming cases',
    description: 'Crack the algorithmic and design interviews using our step-by-step case interactive simulator.',
    appTitle: 'Computer Science Interview',
    appSubtext: 'Ivori • Computer Science',
    defaultIcon: '/logo192.png',
    defaultBanner: '/api/apps/media/tutorials/computer_science_interview/computer_science_interview-1920x1080.png'
  }
];

const getSubjectAbbreviation = (subject) => {
  if (!subject) return 'App';
  const s = subject.toLowerCase().trim();
  if (s.includes('computer science')) return 'CS';
  if (s.includes('medicine')) return 'Med';
  if (s.includes('law')) return 'Law';
  if (s.includes('investment banking')) return 'IB';
  if (s.includes('engineering')) return 'Eng';
  if (s.includes('geography')) return 'Geo';
  if (s.includes('management consulting')) return 'MC';
  if (s.includes('math')) return 'Math';
  if (s.includes('admission')) return 'Adm';
  if (s.includes('reasoning')) return 'Reason';
  if (s.includes('game') || s.includes('gaming')) return 'Game';
  
  const words = subject.split(/\s+/);
  if (words.length > 1) {
    return words.map(w => w[0]).join('').toUpperCase();
  }
  return subject.slice(0, 3).toUpperCase();
};

const formatReviewsCount = (count) => {
  if (!count || count === 0) return '0 reviews';
  if (count >= 1000000) {
    const val = count / 1000000;
    return (val % 1 === 0 ? val.toFixed(0) : val.toFixed(2)) + 'm reviews';
  }
  if (count >= 1000) {
    const val = count / 1000;
    return (val % 1 === 0 ? val.toFixed(0) : val.toFixed(1)) + 'k reviews';
  }
  return count.toString() + ' reviews';
};

const getDeterministicRatingCount = (app) => {
  if (app.rating_count && app.rating_count > 0) return app.rating_count;
  // Deterministic simulation based on app ID or title
  const title = (app.title || '').toLowerCase();
  const id = (app.id || '').toLowerCase();
  if (id === 'cs' || title.includes('algorithmic') || title.includes('think') || title.includes('rebel')) {
    if (title.includes('algorithmic') || id === 'cs') return 6810000;
    return 1240000;
  }
  if (id === 'med' || title.includes('elegance')) {
    return 345000;
  }
  if (id === 'ib' || title.includes('founder') || title.includes('virus')) {
    return 92000;
  }
  // Fallback hash
  let hash = 0;
  const str = id || title || '';
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const simulated = Math.abs(hash % 900) + 100;
  return simulated * 1000;
};

// Masonry Grid Layout Helper Component
function MasonryGrid({ children }) {
  return (
    <div className="landing-masonry-grid">
      {React.Children.map(children, (child) => (
        <div className="landing-masonry-item">
          {child}
        </div>
      ))}
    </div>
  );
}

const LandingAppStore = ({ onNavigate }) => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usePlaceholder, setUsePlaceholder] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null); // null = show all subjects
  const [caseSlideIndex, setCaseSlideIndex] = useState(0);
  const [tutorialSlideIndex, setTutorialSlideIndex] = useState(0);
  const [productivitySlideIndex, setProductivitySlideIndex] = useState(0);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [heroPrompt, setHeroPrompt] = useState('');

  // Custom Subject Dropdown popover state
  const [subjectDropdownOpen, setSubjectDropdownOpen] = useState(false);
  const subjectDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (subjectDropdownRef.current && !subjectDropdownRef.current.contains(e.target)) {
        setSubjectDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Progressive Masonry Batch Loading
  const BATCH_STEPS = [3, 3, 4, 2, 5];
  const [caseBatchStepIndex, setCaseBatchStepIndex] = useState(0);
  const [tutorialBatchStepIndex, setTutorialBatchStepIndex] = useState(0);
  const [interviewBatchStepIndex, setInterviewBatchStepIndex] = useState(0);
  const [specialtyBatchStepIndex, setSpecialtyBatchStepIndex] = useState(0);

  // Scroll controls for Case Study apps (scrolling one item at a time)
  const caseRowRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  // New scroll controls for Events happening now
  const eventsRowRef = useRef(null);
  const [canScrollEventsLeft, setCanScrollEventsLeft] = useState(false);
  const [canScrollEventsRight, setCanScrollEventsRight] = useState(true);

  // Scroll controls for Tutorial apps
  const tutorialRowRef = useRef(null);
  const [canScrollTutorialLeft, setCanScrollTutorialLeft] = useState(false);
  const [canScrollTutorialRight, setCanScrollTutorialRight] = useState(true);

  // Scroll controls for Google Play App Tracks
  const specialtyTrackRef = useRef(null);
  const productivityTrackRef = useRef(null);

  const scrollGplayTrack = (ref, direction) => {
    if (ref && ref.current) {
      ref.current.scrollBy({
        left: direction === 'left' ? -420 : 420,
        behavior: 'smooth',
      });
    }
  };

  // Scroll controls for Productivity & Utility apps
  const productivityRowRef = useRef(null);
  const [canScrollProductivityLeft, setCanScrollProductivityLeft] = useState(false);
  const [canScrollProductivityRight, setCanScrollProductivityRight] = useState(true);

  // Scroll controls for Library apps
  const libraryRowRef = useRef(null);
  const [canScrollLibraryLeft, setCanScrollLibraryLeft] = useState(false);
  const [canScrollLibraryRight, setCanScrollLibraryRight] = useState(true);

  // FAQ Accordion State
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const faqs = [
    {
      q: "What is Ivori and how does it work?",
      a: "Ivori is an AI-powered study ecosystem and case app simulator. It provides interactive legal, medical, coding, business, and academic case studies, step-by-step reasoning tools, and real-time guidance to sharpen your judgment."
    },
    {
      q: "Are all case apps and utilities included in my account?",
      a: "Yes! Access to all case simulators, interactive tutorials, productivity tools, and reference libraries is included with your subscription, allowing unlimited practice across all subjects."
    },
    {
      q: "Can I use Ivori to prepare for university & interview exams?",
      a: "Absolutely. Ivori features specialized preparation modules for LNAT, TMUA, UCAT, MMI Medical Interviews, Investment Banking DCF modeling, Coding IDE labs, and Oxbridge interview circuits."
    },
    {
      q: "How do the AI tutors and reasoning assistants assist my learning?",
      a: "Instead of just giving static answers, Ivori's AI guides your step-by-step logic, points out flaws in reasoning, asks clarifying questions, and provides detailed feedback tailored to exam criteria."
    },
    {
      q: "Is Ivori updated with new case studies and subjects?",
      a: "Yes, our library of case studies, interactive tools, and subject reference libraries is continuously updated with fresh content, real-world case files, and new exam syllabus changes."
    },
    {
      q: "Can I try Ivori before subscribing?",
      a: "Yes! You can explore free sample case studies and utility tools right from the landing app store before creating your account."
    }
  ];

  const updateScrollButtons = () => {
    const el = caseRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const updateEventsScrollButtons = () => {
    const el = eventsRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollEventsLeft(scrollLeft > 10);
      setCanScrollEventsRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const updateTutorialScrollButtons = () => {
    const el = tutorialRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollTutorialLeft(scrollLeft > 10);
      setCanScrollTutorialRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const handleScrollClick = (direction) => {
    const el = caseRowRef.current;
    if (el) {
      const cardWidth = 204; // 180px width + 24px gap between cards
      el.scrollBy({
        left: direction === 'left' ? -cardWidth : cardWidth,
        behavior: 'smooth',
      });
    }
  };

  const handleEventsScrollClick = (direction) => {
    const el = eventsRowRef.current;
    if (el) {
      const cardWidth = 384; // 360px width + 24px gap
      el.scrollBy({
        left: direction === 'left' ? -cardWidth : cardWidth,
        behavior: 'smooth',
      });
    }
  };

  const handleTutorialScrollClick = (direction) => {
    const el = tutorialRowRef.current;
    if (el) {
      const cardWidth = 204; // 180px width + 24px gap between cards
      el.scrollBy({
        left: direction === 'left' ? -cardWidth : cardWidth,
        behavior: 'smooth',
      });
    }
  };

  const updateProductivityScrollButtons = () => {
    const el = productivityRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollProductivityLeft(scrollLeft > 10);
      setCanScrollProductivityRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const handleProductivityScrollClick = (direction) => {
    const el = productivityRowRef.current;
    if (el) {
      const cardWidth = 204; // 180px width + 24px gap between cards
      el.scrollBy({
        left: direction === 'left' ? -cardWidth : cardWidth,
        behavior: 'smooth',
      });
    }
  };

  const updateLibraryScrollButtons = () => {
    const el = libraryRowRef.current;
    if (el) {
      const { scrollLeft, scrollWidth, clientWidth } = el;
      setCanScrollLibraryLeft(scrollLeft > 10);
      setCanScrollLibraryRight(scrollLeft + clientWidth < scrollWidth - 10);
    }
  };

  const handleLibraryScrollClick = (direction) => {
    const el = libraryRowRef.current;
    if (el) {
      const cardWidth = 204; // 180px width + 24px gap between cards
      el.scrollBy({
        left: direction === 'left' ? -cardWidth : cardWidth,
        behavior: 'smooth',
      });
    }
  };

  useEffect(() => {
    if (sideMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [sideMenuOpen]);

  useEffect(() => {
    const fetchApps = async () => {
      try {
        const data = await appsAPI.getAppsPublic();
        let appList = Array.isArray(data) ? data : [];

        // Deduplicate TMUA apps: if multiple TMUA apps exist, keep the main TMUA app and filter out the secondary CS TMUA entry
        const tmuaApps = appList.filter(a => (a.exam_name || '').toLowerCase() === 'tmua' || (a.title || '').toLowerCase().includes('tmua'));
        if (tmuaApps.length > 1) {
          const primaryTmua = tmuaApps.find(a => !(a.subject || '').toLowerCase().includes('computer science')) || tmuaApps[0];
          appList = appList.filter(a => {
            const isTmua = (a.exam_name || '').toLowerCase() === 'tmua' || (a.title || '').toLowerCase().includes('tmua');
            if (isTmua && a.id !== primaryTmua.id) return false;
            return true;
          });
        }

        setApps(appList);
        setUsePlaceholder(appList.length === 0);
      } catch {
        setApps([]);
        setUsePlaceholder(true);
      } finally {
        setLoading(false);
      }
    };

    fetchApps();
  }, []);

  // Reset sliders and scroll position when subject filter or apps load changes
  useEffect(() => {
    setCaseSlideIndex(0);
    setTutorialSlideIndex(0);
    setProductivitySlideIndex(0);
    setCaseBatchStepIndex(0);
    
    const el = caseRowRef.current;
    if (el) {
      el.scrollLeft = 0;
    }
    const elEvents = eventsRowRef.current;
    if (elEvents) {
      elEvents.scrollLeft = 0;
    }
    const elTutorial = tutorialRowRef.current;
    if (elTutorial) {
      elTutorial.scrollLeft = 0;
    }
    const elProductivity = productivityRowRef.current;
    if (elProductivity) {
      elProductivity.scrollLeft = 0;
    }
    const elLibrary = libraryRowRef.current;
    if (elLibrary) {
      elLibrary.scrollLeft = 0;
    }
    const timer = setTimeout(() => {
      updateScrollButtons();
      updateEventsScrollButtons();
      updateTutorialScrollButtons();
      updateProductivityScrollButtons();
      updateLibraryScrollButtons();
    }, 100);
    return () => clearTimeout(timer);
  }, [selectedSubject, apps]);

  useEffect(() => {
    window.addEventListener('resize', updateScrollButtons);
    window.addEventListener('resize', updateEventsScrollButtons);
    window.addEventListener('resize', updateTutorialScrollButtons);
    window.addEventListener('resize', updateProductivityScrollButtons);
    window.addEventListener('resize', updateLibraryScrollButtons);
    return () => {
      window.removeEventListener('resize', updateScrollButtons);
      window.removeEventListener('resize', updateEventsScrollButtons);
      window.removeEventListener('resize', updateTutorialScrollButtons);
      window.removeEventListener('resize', updateProductivityScrollButtons);
      window.removeEventListener('resize', updateLibraryScrollButtons);
    };
  }, []);

  const goToSignIn = () => {
    setSideMenuOpen(false);
    if (onNavigate) onNavigate('login');
  };

  const handleSubjectSelect = (subject) => {
    setSelectedSubject(subject);
    setSideMenuOpen(false);
  };

  const { isAuthenticated } = useAuth();

  const handleAppClick = (appId) => {
    if (!appId) return;

    if (appId === 'ivori-reference-library' || (realLibraryAppFromDb && appId === realLibraryAppFromDb.id)) {
      const targetLibId = realLibraryAppFromDb?.id || libraryAppsToRender[0]?.id || appId;
      if (isAuthenticated) {
        navigate(`/apps-store/${targetLibId}`);
      } else {
        navigate(`/store/${targetLibId}`);
      }
      return;
    }

    let targetApp = apps.find(a => a.id === appId || String(a.id) === String(appId));

    if (!targetApp && typeof appId === 'string' && appId.startsWith('spec-')) {
      const specDefault = defaultSpecialtyApps.find(d => d.id === appId);
      if (specDefault) {
        const matchingDbApp = apps.find(a => 
          (a.subject || '').toLowerCase() === (specDefault.subject || '').toLowerCase() ||
          (a.title || '').toLowerCase().includes((specDefault.subject || '').toLowerCase())
        );
        if (matchingDbApp) {
          targetApp = matchingDbApp;
        }
      }
    }

    const finalId = targetApp ? targetApp.id : appId;
    const appToTest = targetApp || apps.find(a => a.id === appId);
    const isUai = appToTest && ((appToTest.exam_name || '').trim().toLowerCase() === 'uai' ||
      ((appToTest.title || '').trim().toLowerCase() === 'uai'));

    if (isUai) {
      if (!isAuthenticated) {
        navigate('/login', {
          state: {
            intendedAppId: finalId,
            intendedPurchase: true,
            intendedRoute: 'tool',
            intendedToolType: 'uai'
          }
        });
      } else {
        navigate('/chat');
      }
      return;
    }

    if (isAuthenticated) {
      navigate(`/apps-store/${finalId}`);
    } else {
      navigate(`/store/${finalId}`);
    }
  };

  // Build data: use real apps if available, else placeholders
  // Experience = case study apps only (exclude productivity and tool)
  const caseAppsFromApi = apps.filter((app) => app.app_type === 'case');
  const allExperienceApps = (usePlaceholder || caseAppsFromApi.length === 0)
    ? PLACEHOLDER_EXPERIENCE
    : caseAppsFromApi;
  
  // Sort: Windmill game first, then others
  const experienceApps = [...allExperienceApps].sort((a, b) => {
    const aIsWindmill = a.creator === 'Ash.exe' && (a.subject === 'Game' || a.subject === 'Entertainment' || a.subject === 'Gaming');
    const bIsWindmill = b.creator === 'Ash.exe' && (b.subject === 'Game' || b.subject === 'Entertainment' || b.subject === 'Gaming');
    if (aIsWindmill && !bIsWindmill) return -1;
    if (!aIsWindmill && bIsWindmill) return 1;
    return 0;
  });
  
  // Productivity apps (CV Builder, Cover Letter, My Path, etc.) + utility
  const productivityApps = usePlaceholder
    ? PLACEHOLDER_PRODUCTIVITY
    : apps.filter(
        (app) =>
          (app.app_type === 'tutorial' || app.app_type === 'productivity' || app.app_type === 'utility') &&
          !isHiddenTaskListApp(app)
      );

  // Tutorial-only (exam prep, reasoning) and productivity+utility (tools) for separate boxes
  const tutorialOnlyApps = productivityApps.filter((app) => app.app_type === 'tutorial');
  const productivityAndUtilityApps = productivityApps.filter(
    (app) => (app.app_type === 'productivity' || app.app_type === 'utility') && 
             (app.exam_name || '').toLowerCase() !== 'library'
  );
  const libraryApps = productivityApps.filter(
    (app) => (app.app_type === 'productivity' || app.app_type === 'utility') && 
             (app.exam_name || '').toLowerCase() === 'library'
  );

  // Filter apps based on selected subject
  const filteredExperienceApps = selectedSubject
    ? experienceApps.filter((app) => (app.subject || '').toLowerCase() === selectedSubject.toLowerCase())
    : experienceApps;
  // For productivity: when subject selected, show subject-specific + tool apps (no subject)
  const filteredProductivityApps = selectedSubject
    ? [
        ...productivityApps.filter(
          (app) => (app.subject || '').toLowerCase() === selectedSubject.toLowerCase()
        ),
        ...productivityApps.filter((app) => app.app_type === 'productivity' || app.app_type === 'utility'),
      ]
    : productivityApps;

  // Group all case apps by subject
  const appsBySubjectAll = experienceApps.reduce((acc, app) => {
    const subject = app.subject || 'Other';
    if (!acc[subject]) acc[subject] = [];
    acc[subject].push(app);
    return acc;
  }, {});
  const sliderSubjectsAll = Object.keys(appsBySubjectAll);

  // Find reasoning test apps from database (productivity apps with reasoning flags but no case_study_id)
  // These are not subject-specific and should appear for all subjects
  const getReasoningTestApps = () => {
    if (usePlaceholder) return [];
    
    // Find tutorial/productivity apps that are reasoning-only (no case study, has reasoning flags)
    const reasoningOnlyApps = productivityApps.filter((app) => {
      if (app.app_type !== 'tutorial' && app.app_type !== 'productivity') return false;
      // Must not have a case study (reasoning-only)
      if (app.case_study_id) return false;
      // Must have at least one reasoning flag
      return !!(app.has_logical_reasoning || app.has_pattern_recognition || 
                app.has_numerical_reasoning || app.has_verbal_reasoning);
    });
    
    // Map to the 4 reasoning types - find one app for each type
    const reasoningTestsMap = {
      'Logical Reasoning': null,
      'Pattern Recognition': null,
      'Numerical Reasoning': null,
      'Verbal Reasoning': null,
    };
    
    reasoningOnlyApps.forEach((app) => {
      // Find which reasoning type(s) this app has
      if (app.has_logical_reasoning && !reasoningTestsMap['Logical Reasoning']) {
        reasoningTestsMap['Logical Reasoning'] = {
          ...app,
          exam_name: 'Logical',
          title: 'Logical',
        };
      }
      if (app.has_pattern_recognition && !reasoningTestsMap['Pattern Recognition']) {
        reasoningTestsMap['Pattern Recognition'] = {
          ...app,
          exam_name: 'Pattern',
          title: 'Pattern',
        };
      }
      if (app.has_numerical_reasoning && !reasoningTestsMap['Numerical Reasoning']) {
        reasoningTestsMap['Numerical Reasoning'] = {
          ...app,
          exam_name: 'Numerical',
          title: 'Numerical',
        };
      }
      if (app.has_verbal_reasoning && !reasoningTestsMap['Verbal Reasoning']) {
        reasoningTestsMap['Verbal Reasoning'] = {
          ...app,
          exam_name: 'Verbal',
          title: 'Verbal',
        };
      }
    });
    
    // Return only the apps that were found (filter out nulls)
    return Object.values(reasoningTestsMap).filter(app => app !== null);
  };

  const reasoningTestApps = getReasoningTestApps();

  // Get tutorial apps by subject (tutorial + reasoning tests for that subject)
  const getTutorialAppsForSubject = (subject) => {
    const subjectTutorial = tutorialOnlyApps.filter((app) => {
      const appSubject = app.subject || '';
      const isCaius = (app.exam_name || '').toLowerCase() === 'caius_explore' || (app.title || '').toLowerCase() === 'caius explore';
      return isCaius || appSubject.toLowerCase() === subject.toLowerCase();
    });
    const reasoningTests = reasoningTestApps.map((test) => ({ ...test, subject }));
    return [...subjectTutorial, ...reasoningTests];
  };

  const tutorialAppsToShow = selectedSubject
    ? getTutorialAppsForSubject(selectedSubject)
    : tutorialOnlyApps;

  // Productivity (tool) apps: same set for all subjects – productivity + utility
  const productivityAppsToShow = productivityAndUtilityApps;

  // Diverse subject reordering helper for front page showcase
  const sortDiverseCaseApps = (appList) => {
    if (!appList || appList.length === 0) return appList;
    if (selectedSubject) return appList;

    const bySubject = {};
    appList.forEach(app => {
      const subj = (app.subject || 'Other').trim();
      if (!bySubject[subj]) bySubject[subj] = [];
      bySubject[subj].push({ ...app });
    });

    const result = [];
    const addedIds = new Set();

    const pushApp = (app) => {
      if (app) {
        const key = app.id || app.title;
        if (!addedIds.has(key)) {
          addedIds.add(key);
          result.push(app);
        }
      }
    };

    // Priority 1: Law
    const lawKey = Object.keys(bySubject).find(k => k.toLowerCase().includes('law'));
    if (lawKey && bySubject[lawKey].length > 0) {
      pushApp(bySubject[lawKey].shift());
    }

    // Priority 2: Mathematics / Maths
    const mathKey = Object.keys(bySubject).find(k => k.toLowerCase().includes('math'));
    if (mathKey && bySubject[mathKey].length > 0) {
      pushApp(bySubject[mathKey].shift());
    }

    // Priority 3: Investment Banking / Finance
    const financeKey = Object.keys(bySubject).find(k => 
      k.toLowerCase().includes('banking') || 
      k.toLowerCase().includes('finance') || 
      k.toLowerCase().includes('investment') || 
      k.toLowerCase().includes('business')
    );
    if (financeKey && bySubject[financeKey].length > 0) {
      pushApp(bySubject[financeKey].shift());
    }

    // Interleave 1 app from each remaining subject filter
    const subjectKeys = Object.keys(bySubject);
    let hasMore = true;
    while (hasMore) {
      hasMore = false;
      for (const key of subjectKeys) {
        if (bySubject[key] && bySubject[key].length > 0) {
          pushApp(bySubject[key].shift());
          hasMore = true;
        }
      }
    }

    appList.forEach(app => pushApp(app));
    return result;
  };

  // Determine which case apps to show in the Case apps box
  const caseAppsToRender = sortDiverseCaseApps(filteredExperienceApps);

  // Tutorial apps pool
  const tutorialAppsToRender = [...tutorialAppsToShow].sort((a, b) => {
    const aIsCaius = (a.exam_name || '').toLowerCase() === 'caius_explore' || (a.title || '').toLowerCase() === 'caius explore';
    const bIsCaius = (b.exam_name || '').toLowerCase() === 'caius_explore' || (b.title || '').toLowerCase() === 'caius explore';
    if (aIsCaius && !bIsCaius) return -1;
    if (!aIsCaius && bIsCaius) return 1;
    return 0;
  });

  // Split Tutorial apps into Interview apps & Specialty apps
  const isInterviewApp = (app) => {
    const text = `${app.title || ''} ${app.subject || ''} ${app.description || ''} ${app.exam_name || ''}`.toLowerCase();
    return text.includes('interview') || text.includes('mmi') || text.includes('oxford') || text.includes('cambridge') || text.includes('lnat') || text.includes('ucat') || text.includes('admission') || text.includes('entrance') || text.includes('caius') || text.includes('prep');
  };

  const interviewAppsToRender = tutorialAppsToRender.filter(app => isInterviewApp(app));

  // Curated Specialty Apps for Computer Science, Chemistry, Biology, Law, Finance
  const defaultSpecialtyApps = [
    {
      id: 'spec-cs-python',
      title: 'Python IDE & Algorithm Sandbox',
      subject: 'Computer Science',
      description: 'Interactive Python code runner, algorithm visualizer, and data structure debugger with instant AI execution feedback.',
      images: { medium: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?w=800&auto=format&fit=crop&q=80' }
    },
    {
      id: 'spec-chem-flow',
      title: 'Organic Chemistry & Molecular Reactions Lab',
      subject: 'Chemistry',
      description: 'Simulate organic synthesis mechanisms, thermodynamic enthalpy changes, and molecular structure bonding 3D models.',
      images: { medium: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?w=800&auto=format&fit=crop&q=80' }
    },
    {
      id: 'spec-bio-genetics',
      title: 'CRISPR Genetics & Cellular Pathology Lab',
      subject: 'Biology & Medicine',
      description: 'Explore gene expression pathways, DNA sequence alignments, and cellular disease response simulations.',
      images: { medium: 'https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=800&auto=format&fit=crop&q=80' }
    },
    {
      id: 'spec-law-statute',
      title: 'Statutory Precedent & Constitutional Case Analyzer',
      subject: 'Law',
      description: 'Analyze Landmark Supreme Court rulings, ratio decidendi structures, and statutory interpretation frameworks.',
      images: { medium: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&auto=format&fit=crop&q=80' }
    },
    {
      id: 'spec-finance-dcf',
      title: 'Financial Modeling & Quant Valuation Engine',
      subject: 'Finance & Economics',
      description: 'Build LBO valuation models, discounted cash flow (DCF) projections, and Monte Carlo risk simulations.',
      images: { medium: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=800&auto=format&fit=crop&q=80' }
    }
  ];

  const rawSpecialtyApps = tutorialAppsToRender.filter(app => {
    if (isInterviewApp(app)) return false;
    const examNameLower = (app.exam_name || '').trim().toLowerCase();
    const subjectLower = (app.subject || '').trim().toLowerCase();
    if (examNameLower === 'tmua' && (subjectLower.includes('computer science') || subjectLower.includes('cs'))) {
      return false;
    }
    return true;
  });

  const specialtyAppsToRender = rawSpecialtyApps.length >= 3 
    ? [...rawSpecialtyApps, ...defaultSpecialtyApps.filter(d => !rawSpecialtyApps.some(r => (r.title || '').toLowerCase() === d.title.toLowerCase()))]
    : defaultSpecialtyApps;

  // Dynamic fetch of real Library app from database
  const realLibraryAppFromDb = apps.find(
    (app) =>
      app.app_type === 'library' ||
      (app.exam_name || '').toLowerCase() === 'library' ||
      (app.title || '').toLowerCase() === 'ivori reference library' ||
      (app.title || '').toLowerCase() === 'smart reference library'
  ) || apps.find(app => (app.title || '').toLowerCase().includes('library'));

  const libraryImage = realLibraryAppFromDb?.images?.medium ||
    realLibraryAppFromDb?.images?.small ||
    realLibraryAppFromDb?.images?.large ||
    '/logo-yellow.png';

  const librarySingleCard = {
    id: realLibraryAppFromDb?.id || 'ivori-reference-library',
    title: realLibraryAppFromDb?.title || 'ivori Reference Library',
    subject: realLibraryAppFromDb?.subject || 'Library & Books',
    description: realLibraryAppFromDb?.description || 'Access the complete in-app digital library of textbooks, legal frameworks, medical guides, and reference books.',
    is_coming_soon: false,
    is_free: true,
    pricing_tier: 'free',
    price: 0,
    images: { medium: libraryImage }
  };

  const productivityAppsToRender = [librarySingleCard, ...productivityAppsToShow];

  const libraryAppsToShow = selectedSubject
    ? libraryApps.filter((app) => (app.subject || '').toLowerCase() === selectedSubject.toLowerCase())
    : libraryApps;

  const libraryAppsToRender = [...libraryAppsToShow].sort((a, b) => 
    (a.title || '').localeCompare(b.title || '')
  );

  // Helper to detect if an app is in Coming Soon state (Task List, Flow Forge, Code Lab)
  const isAppComingSoon = (appItem) => {
    if (!appItem) return false;
    if (appItem.is_coming_soon || appItem.coming_soon || appItem.status === 'coming_soon') return true;
    const titleLower = (appItem.title || '').toLowerCase();
    const examLower = (appItem.exam_name || '').toLowerCase();
    const idLower = (appItem.id || '').toLowerCase();
    return (
      titleLower.includes('task list') ||
      examLower.includes('task_list') ||
      idLower.includes('task-list') ||
      idLower.includes('task_list') ||
      titleLower.includes('flow forge') ||
      titleLower.includes('flowforge') ||
      examLower.includes('flow_forge') ||
      idLower.includes('flow_forge') ||
      idLower.includes('flow-forge') ||
      idLower.includes('flowforge') ||
      titleLower.includes('code lab') ||
      titleLower.includes('codelab') ||
      titleLower.includes('python code') ||
      idLower.includes('code-lab') ||
      idLower.includes('code_lab') ||
      idLower.includes('codelab') ||
      idLower.includes('spec-cs-python') ||
      titleLower.includes('coming soon')
    );
  };

  const scrollSection = (sectionId, direction) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollBy({
        left: direction === 'left' ? -400 : 400,
        behavior: 'smooth',
      });
    }
  };

  /*
  if (loading) {
    return (
      <div className="landing-app-store app-store-view netflix-style">
        <main className="landing-app-store-main app-store-main">
          <div className="app-store-loading">
            <div className="loading-spinner" />
            <p>Loading...</p>
          </div>
        </main>
      </div>
    );
  }
  */

  // Get all unique subjects for navigation
  const allSubjects = experienceApps.reduce((acc, app) => {
    const subject = app.subject || 'Other';
    if (subject && !acc.includes(subject)) {
      acc.push(subject);
    }
    return acc;
  }, []);

  return (
    <div className="landing-app-store app-store-view netflix-style">
      <SeoMeta 
        title="Interactive AI Case Sandboxes & Apps Store | ivori"
        description="Explore interactive AI sandboxes for Law, Medicine, Finance, Chemistry, and Computer Science. Run in-browser Pyodide Python execution, DCF valuation models, and statutory precedent trees."
        keywords="AI case sandbox, interactive app store, Pyodide Python lab, DCF model, statutory analyzer, ivori apps"
        url={
          isEduPortal() ||
          window.location.hostname.includes('edu') ||
          window.location.hostname === 'pathbuddy.com' ||
          window.location.hostname === 'www.pathbuddy.com'
            ? appUrl('/apps')
            : 'https://ivoriai.com/edu/apps'
        }
      />
      <main className="landing-app-store-main app-store-main">
        {/* ================= HERO ================= */}
        <section className="hero" id="hero">
          <img src={heroSunsetImg} alt="Pathbuddy Golden Sunset Hero Background" className="hero-bg-img" aria-hidden="true" />
          <div className="hero-overlay" aria-hidden="true" />
          <div className="hero-content">
            <h1 className="hero-title">
              AI that sharpens judgment, reasoning, decisions.
            </h1>
            <p className="hero-sub">
              From instant AI chat and deep reasoning to interactive case sandboxes and automated workflows — <strong>ivori</strong> provides the intelligence to solve your hardest problems.
              <span className="hero-sub-edu-link" style={{ display: 'block', marginTop: '10px', fontSize: '14.5px', opacity: 0.9 }}>
                Looking for student learning & exam prep? Visit <a href="https://ivoriai.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--iv-accent)', fontWeight: 700, textDecoration: 'underline' }}>ivoriai.com ↗</a>
              </span>
            </p>

            {/* Animated Prompt Questions Marquee (Hidden per user request) */}
            {/* 
            <div style={{ width: '100vw', position: 'relative', left: '50%', right: '50%', marginLeft: '-50vw', marginRight: '-50vw', marginBottom: '28px', overflow: 'hidden' }}>
              <PromptMarquee />
            </div>
            */}

            {/* Interactive Morphing Prompt Widget */}
            <HeroPromptWidget
              onSubmit={() => goToSignIn()}
              onExploreClick={() => {
                const el = document.getElementById('case-apps');
                if (el) {
                  const topOffset = el.getBoundingClientRect().top + window.pageYOffset - 10;
                  window.scrollTo({ top: topOffset, behavior: 'smooth' });
                }
              }}
            />
          </div>
        </section>



        {/* Side menu (mobile & tablet): filters + Sign In */}
        <div
          className={`landing-side-menu-backdrop ${sideMenuOpen ? 'landing-side-menu-backdrop-open' : ''}`}
          onClick={() => setSideMenuOpen(false)}
          onKeyDown={(e) => e.key === 'Escape' && setSideMenuOpen(false)}
          role="button"
          tabIndex={-1}
          aria-label="Close menu"
        />
        <aside
          className={`landing-side-menu ${sideMenuOpen ? 'landing-side-menu-open' : ''}`}
          aria-label="Menu"
          aria-hidden={!sideMenuOpen}
        >
          <div className="landing-side-menu-inner">
            <button
              type="button"
              className="landing-side-menu-close"
              onClick={() => setSideMenuOpen(false)}
              aria-label="Close menu"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <h2 className="landing-side-menu-title">All subjects to filter</h2>
            <nav className="landing-side-menu-nav">
              <button
                type="button"
                className={`subject-nav-button ${selectedSubject === null ? 'active' : ''}`}
                onClick={() => handleSubjectSelect(null)}
              >
                All
              </button>
              {allSubjects.map((subject) => (
                <button
                  key={subject}
                  type="button"
                  className={`subject-nav-button ${selectedSubject === subject ? 'active' : ''}`}
                  onClick={() => handleSubjectSelect(subject)}
                >
                  {subject}
                </button>
              ))}
            </nav>
            <div className="landing-side-menu-signin">
              <button
                type="button"
                className="btn btn-secondary landing-side-menu-signin-btn"
                onClick={goToSignIn}
              >
                Sign In
              </button>
            </div>
          </div>
        </aside>

        <div className="app-store-content">
          {/* Full-Width Dark Cinematic Case Apps Section */}
          <section id="case-apps" className="case-apps-fullwidth-section">
            <div className="case-apps-fullwidth-container">
              {(() => {
                  // Deduplicate items cleanly by ID/title to prevent any duplicates
                  const uniqueCaseApps = [];
                  const seenIds = new Set();
                  caseAppsToRender.forEach(app => {
                    const key = app.id || app.title;
                    if (!seenIds.has(key)) {
                      seenIds.add(key);
                      uniqueCaseApps.push(app);
                    }
                  });

                  // Batch loading sequence: initial load shows 24 cases (6 full rows of 4 columns) so all 4 columns are 100% filled
                  const BATCH_STEP_PATTERN = [24, 4, 4, 4, 4];
                  const getStepBatchSize = (step) => {
                    if (step === 0) return 24;
                    const patternIndex = ((step - 1) % (BATCH_STEP_PATTERN.length - 1)) + 1;
                    return BATCH_STEP_PATTERN[patternIndex];
                  };

                  let visibleCaseCount = 0;
                  for (let i = 0; i <= caseBatchStepIndex; i++) {
                    visibleCaseCount += getStepBatchSize(i);
                  }

                  const visibleCaseApps = uniqueCaseApps.slice(0, visibleCaseCount);
                  const hasMoreCases = visibleCaseCount < uniqueCaseApps.length;
                  const nextBatchSize = Math.min(getStepBatchSize(caseBatchStepIndex + 1), uniqueCaseApps.length - visibleCaseCount);

                  // Distribute visible cases into 4 vertical columns evenly
                  const caseColumns = [[], [], [], []];
                  visibleCaseApps.forEach((app, idx) => {
                    caseColumns[idx % 4].push({ app, index: idx });
                  });

                  // Even column start offsets for clean horizontal alignment
                  const COLUMN_TOP_OFFSETS = [0, 0, 0, 0];

                  // Helper to detect if an app is in Coming Soon state (Task List, Flow Forge & Code Lab)
                  const isAppComingSoon = (appItem) => {
                    if (!appItem) return false;
                    if (appItem.is_coming_soon || appItem.coming_soon || appItem.status === 'coming_soon') return true;
                    const titleLower = (appItem.title || '').toLowerCase();
                    const examLower = (appItem.exam_name || '').toLowerCase();
                    const idLower = (appItem.id || '').toLowerCase();
                    return (
                      titleLower.includes('task list') ||
                      examLower.includes('task_list') ||
                      idLower.includes('task-list') ||
                      idLower.includes('task_list') ||
                      titleLower.includes('flow forge') ||
                      titleLower.includes('flowforge') ||
                      titleLower.includes('flow') ||
                      titleLower.includes('forge') ||
                      examLower.includes('flow_forge') ||
                      idLower.includes('flow_forge') ||
                      idLower.includes('flow-forge') ||
                      idLower.includes('flowforge') ||
                      titleLower.includes('code lab') ||
                      titleLower.includes('codelab') ||
                      titleLower.includes('python code') ||
                      idLower.includes('code-lab') ||
                      idLower.includes('code_lab') ||
                      idLower.includes('codelab') ||
                      idLower.includes('spec-cs-python') ||
                      titleLower.includes('coming soon')
                    );
                  };

                  return (
                    <>
                      {loading ? (
                        <SkeletonLoader type="landscape-cards" count={4} style={{ padding: 0 }} />
                      ) : (
                        <div className="case-columns-container">
                          {caseColumns.map((colItems, colIdx) => (
                            <div 
                              key={`case-col-${colIdx}`} 
                              className="case-column-stack"
                              style={{ marginTop: `${COLUMN_TOP_OFFSETS[colIdx % 4]}px` }}
                            >
                              {colItems.map(({ app, index }) => {
                                const rawImg = app.images?.medium || app.images?.large || app.images?.small;
                                const imageUrl = (rawImg && rawImg !== '/logo192.png') ? rawImg : '/logo-yellow.png';
                                const rawDesc = app.description || app.tagline || app.short_description || '';
                                let cleanDesc = rawDesc.replace(/<[^>]*>?/gm, '').trim();
                                if (!cleanDesc) {
                                  cleanDesc = `Explore real-world case studies, critical decision frameworks, and analytical practice in ${app.subject || app.title}.`;
                                }
                                const isComingSoon = isAppComingSoon(app);

                                return (
                                  <div
                                    key={app.id ? `case-app-${app.id}` : `case-idx-${index}`}
                                    role="button"
                                    tabIndex={0}
                                    className={`masonry-case-card ${isComingSoon ? 'coming-soon' : ''}`}
                                    onClick={() => !isComingSoon && handleAppClick(app.id)}
                                    onKeyDown={(e) => e.key === 'Enter' && !isComingSoon && handleAppClick(app.id)}
                                  >
                                    <div className="masonry-card-image-wrapper">
                                      <img 
                                        src={imageUrl} 
                                        alt={app.title} 
                                        className="masonry-card-image" 
                                        onError={(e) => {
                                          e.target.onerror = null;
                                          e.target.src = '/logo-yellow.png';
                                        }}
                                      />
                                      {isComingSoon ? (
                                        <div className="coming-soon-overlay">
                                          <span className="coming-soon-badge">Coming Soon</span>
                                        </div>
                                      ) : (
                                        app.subject && (
                                          <span className="masonry-card-subject-tag">{app.subject}</span>
                                        )
                                      )}
                                    </div>
                                    <div className="masonry-card-body">
                                      <h3 className="masonry-card-title">{app.title}</h3>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Progressive Explore More Button */}
                      <div className="masonry-explore-more-wrapper">
                        {hasMoreCases ? (
                          <button
                            type="button"
                            className="masonry-explore-more-btn"
                            onClick={() => setCaseBatchStepIndex(prev => prev + 1)}
                          >
                            <span>Explore More Cases</span>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M6 9l6 6 6-6"/>
                            </svg>
                          </button>
                        ) : uniqueCaseApps.length > 3 ? (
                          <button
                            type="button"
                            className="masonry-explore-more-btn show-less"
                            onClick={() => setCaseBatchStepIndex(0)}
                          >
                            <span>Show Less</span>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                              <path d="M18 15l-6-6-6 6"/>
                            </svg>
                          </button>
                        ) : null}
                      </div>
                    </>
                  );
                })()}
            </div>
          </section>

          {/* Google Play Style Specialized AI Agents & Tools Section */}
          <section id="specialty-apps" className="case-apps-fullwidth-section specialty-apps-fullwidth-section" style={{ marginTop: '48px' }}>
            <div className="case-apps-fullwidth-container">
              {/* Dynamic 100% Full Width Grid (Left to Right) */}
              <div className="gplay-apps-grid">
                {(() => {
                  const uniqueSpecialtyApps = [];
                  const seenSpecKeys = new Set();
                  specialtyAppsToRender.forEach(app => {
                    const key = ((app.exam_name || app.title || app.id) + '').trim().toLowerCase();
                    if (!seenSpecKeys.has(key)) {
                      seenSpecKeys.add(key);
                      uniqueSpecialtyApps.push(app);
                    }
                  });

                  return uniqueSpecialtyApps.map((app, index) => {
                    const imageUrl = app.images?.medium || app.images?.large || app.images?.small || '/logo-yellow.png';
                    const isComingSoon = isAppComingSoon(app);

                    return (
                      <div
                        key={app.id ? `gplay-app-${app.id}` : `gplay-idx-${index}`}
                        className={`gplay-app-card ${isComingSoon ? 'coming-soon' : ''}`}
                        onClick={() => !isComingSoon && handleAppClick(app.id)}
                      >
                        <div className="gplay-app-icon-wrapper">
                          <img 
                            src={imageUrl} 
                            alt={app.title} 
                            className="gplay-app-icon-img"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/logo-yellow.png';
                            }}
                          />
                          {isComingSoon && (
                            <div className="gplay-coming-soon-overlay">
                              <span>Coming Soon</span>
                            </div>
                          )}
                        </div>
                        <div className="gplay-app-title">{app.title}</div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </section>

          {/* AI Productivity & Utilities Section (Dynamic 100% Full Width Grid) */}
          <section id="productivity-apps" className="case-apps-fullwidth-section specialty-apps-fullwidth-section" style={{ marginTop: '48px' }}>
            <div className="case-apps-fullwidth-container">
              <div className="gplay-apps-grid">
                {productivityAppsToRender.map((app, index) => {
                  const imageUrl = app.images?.medium || app.images?.large || app.images?.small || '/logo-yellow.png';
                  const isComingSoon = isAppComingSoon(app);

                  return (
                    <div
                      key={app.id ? `prod-app-${app.id}` : `prod-idx-${index}`}
                      className={`gplay-app-card ${isComingSoon ? 'coming-soon' : ''}`}
                      onClick={() => !isComingSoon && handleAppClick(app.id)}
                    >
                      <div className="gplay-app-icon-wrapper">
                        <img 
                          src={imageUrl} 
                          alt={app.title} 
                          className="gplay-app-icon-img"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/logo-yellow.png';
                          }}
                        />
                        {isComingSoon && (
                          <div className="gplay-coming-soon-overlay">
                            <span>Coming Soon</span>
                          </div>
                        )}
                      </div>
                      <div className="gplay-app-title">{app.title}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Roadblocks Marquee & 4 Feature Highlights Section (Commented out per user request) */}
          {/* <RoadblocksFeatureSection /> */}

          {/* Full-Width Dark Cinematic Interview Apps Section */}
          <section id="interview-apps" className="case-apps-fullwidth-section interview-apps-fullwidth-section" style={{ marginTop: '48px' }}>
            <div className="case-apps-fullwidth-container">
              {(() => {
                // Deduplicate interview apps cleanly
                const uniqueInterviewApps = [];
                const seenIntIds = new Set();
                interviewAppsToRender.forEach(app => {
                  const key = app.id || app.title;
                  if (!seenIntIds.has(key)) {
                    seenIntIds.add(key);
                    uniqueInterviewApps.push(app);
                  }
                });

                // Distribute visible interview apps into 4 vertical columns evenly
                const interviewColumns = [[], [], [], []];
                uniqueInterviewApps.forEach((app, idx) => {
                  interviewColumns[idx % 4].push({ app, index: idx });
                });

                // Clean even start offsets across all columns
                const COLUMN_TOP_OFFSETS = [0, 0, 0, 0];

                const formatAppDisplayTitle = (title) => {
                  if (!title) return '';
                  const t = title.trim();
                  if (t.toLowerCase() === 'lnat') return 'LNAT';
                  if (t.toLowerCase() === 'tmua') return 'TMUA';
                  if (t.toLowerCase() === 'ucat') return 'UCAT';
                  if (t.toLowerCase() === 'esat') return 'ESAT';
                  if (t.toLowerCase() === 'tara') return 'TARA';
                  return t;
                };

                return (
                  <>
                    {loading ? (
                      <SkeletonLoader type="landscape-cards" count={4} style={{ padding: 0 }} />
                    ) : (
                      <div className="case-columns-container">
                        {interviewColumns.map((colItems, colIdx) => (
                          <div 
                            key={`int-col-${colIdx}`} 
                            className="case-column-stack"
                            style={{ marginTop: `${COLUMN_TOP_OFFSETS[colIdx % 4]}px` }}
                          >
                            {colItems.map(({ app, index }) => {
                              const imageUrl = app.images?.medium || app.images?.large || app.images?.small || '/logo-yellow.png';
                              const rawDesc = app.description || app.tagline || app.short_description || '';
                              let cleanDesc = rawDesc.replace(/<[^>]*>?/gm, '').trim();
                              if (!cleanDesc) {
                                cleanDesc = `Practice strategic roleplay, executive interviews, and professional scenario simulations with ${app.title}.`;
                              }
                              const isComingSoon = isAppComingSoon(app);

                              return (
                                <div
                                  key={app.id ? `interview-app-${app.id}` : `int-idx-${index}`}
                                  role="button"
                                  tabIndex={0}
                                  className={`masonry-case-card ${isComingSoon ? 'coming-soon' : ''}`}
                                  onClick={() => !isComingSoon && handleAppClick(app.id)}
                                  onKeyDown={(e) => e.key === 'Enter' && !isComingSoon && handleAppClick(app.id)}
                                >
                                  <div className="masonry-card-image-wrapper">
                                    <img 
                                      src={imageUrl} 
                                      alt={app.title} 
                                      className="masonry-card-image" 
                                      onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.src = '/logo-yellow.png';
                                      }}
                                    />
                                    {isComingSoon ? (
                                      <div className="coming-soon-overlay">
                                        <span className="coming-soon-badge">Coming Soon</span>
                                      </div>
                                    ) : (
                                      app.subject && (
                                        <span className="masonry-card-subject-tag">{app.subject}</span>
                                      )
                                    )}
                                  </div>
                                  <div className="masonry-card-body">
                                    <h3 className="masonry-card-title">{formatAppDisplayTitle(app.title)}</h3>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          </section>



          {/* Multi-book Libraries Section (Commented out: replaced by single Ivori Reference Library card in AI Productivity & Utilities) */}
          {/*
          {(libraryAppsToRender.length > 0 || loading) && (
            <section id="library-apps" className="column-box productivity-box case-apps-row-box" style={{ marginTop: '36px' }}>
              <h2 className="column-box-title">Libraries</h2>
              <div className="case-apps-row-wrapper">
                <button
                  type="button"
                  className="case-scroll-btn case-scroll-btn-left"
                  onClick={() => handleLibraryScrollClick('left')}
                  disabled={!canScrollLibraryLeft}
                  aria-label="Scroll left"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div 
                  className="case-apps-row" 
                  id="library-apps-row"
                  ref={libraryRowRef}
                  onScroll={updateLibraryScrollButtons}
                >
                  {loading ? (
                    <SkeletonLoader type="squircle-cards" count={4} style={{ padding: 0 }} />
                  ) : (
                    libraryAppsToRender.map((app) => {
                      const imageUrl =
                        app.images?.medium || app.images?.large || app.images?.small || '/uai-icon.png';
                      return (
                        <div
                          key={app.id}
                          role="button"
                          tabIndex={0}
                          className="column-box-item"
                          onClick={() => handleAppClick(app.id)}
                          onKeyDown={(e) => e.key === 'Enter' && handleAppClick(app.id)}
                        >
                          <img 
                            src={imageUrl} 
                            alt={app.title} 
                            className="column-box-icon" 
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = '/uai-icon.png';
                            }}
                          />
                          <div className="column-box-info">
                            <h3 className="column-box-item-title">{app.title}</h3>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <button
                  type="button"
                  className="case-scroll-btn case-scroll-btn-right"
                  onClick={() => handleLibraryScrollClick('right')}
                  disabled={!canScrollLibraryRight}
                  aria-label="Scroll right"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </section>
          )}
          */}

          {/* Ivori Solutions & Audiences Section (Research, Governance, Learning, Policy Makers) - Commented out per user request */}
          {/* 
          <section id="ivori-solutions" className="ivori-solutions-section">
            <div className="ivori-solutions-container">
              <div className="ivori-solutions-header">
                <span className="ivori-subhead-pill">Institutional Solutions</span>
                <h2 className="ivori-solutions-title">Ivori for Research, Governance, Learning & Policy</h2>
                <p className="ivori-solutions-subtitle">
                  Custom-built AI reasoning engines tailored for rigorous academic research, public governance, interactive learning, and evidence-based policy making.
                </p>
              </div>

              <div className="ivori-solutions-grid">
                <div className="ivori-card">
                  <span className="ivori-card-index">01</span>
                  <h3 className="ivori-card-title">Ivori for Research</h3>
                  <p className="ivori-card-desc">
                    Accelerate literature synthesis, empirical data modeling, citation verification, and super-curricular research projects with mathematical proof integrity.
                  </p>
                  <ul className="ivori-card-features">
                    <li>• Automated citation graph analysis & peer-review checks</li>
                    <li>• Quantitative data modeling and LaTeX proof synthesis</li>
                    <li>• Multi-document contextual extraction & hypothesis testing</li>
                  </ul>
                </div>

                <div className="ivori-card">
                  <span className="ivori-card-index">02</span>
                  <h3 className="ivori-card-title">Ivori for Governance</h3>
                  <p className="ivori-card-desc">
                    Rigorous policy evaluation, regulatory compliance tracking, risk modeling, and institutional oversight frameworks for public and corporate bodies.
                  </p>
                  <ul className="ivori-card-features">
                    <li>• Regulatory impact analysis & compliance mapping</li>
                    <li>• Institutional risk auditing and scenario stress-testing</li>
                    <li>• Transparent algorithmic oversight & bias detection</li>
                  </ul>
                </div>

                <div className="ivori-card">
                  <span className="ivori-card-index">03</span>
                  <h3 className="ivori-card-title">Ivori for Learning</h3>
                  <p className="ivori-card-desc">
                    Personalized Socratic tutoring, university entrance exam simulation (TMUA, LNAT), step-by-step logic proof breakdowns, and mastery analytics.
                  </p>
                  <ul className="ivori-card-features">
                    <li>• Pearson VUE exam simulator with real-time feedback</li>
                    <li>• Oxford & Cambridge rubric-aligned essay grading</li>
                    <li>• Topic weakness heatmaps & personalized learning paths</li>
                  </ul>
                </div>

                <div className="ivori-card">
                  <span className="ivori-card-index">04</span>
                  <h3 className="ivori-card-title">Ivori for Policy Makers</h3>
                  <p className="ivori-card-desc">
                    Statutory draft evaluation, economic impact modeling, legislative argument synthesis, and real-time policy simulation sandboxes.
                  </p>
                  <ul className="ivori-card-features">
                    <li>• Legislative draft comparison & semantic flaw detection</li>
                    <li>• Macro-economic impact forecasting & stakeholder models</li>
                    <li>• Harvard Case Method policy briefs & executive summaries</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>
          */}





          {/* Frequently Asked Questions Section - Commented out per user request */}
          {/* 
          <section id="faq-section" className="faq-airy-section">
            <div className="faq-airy-container">
              <div className="faq-airy-header">
                <h2 className="faq-airy-title">Frequently Asked Questions</h2>
                <p className="faq-airy-subtitle">Got questions? We've got answers to help you get started.</p>
              </div>

              <div className="faq-airy-list">
                {faqs.map((faq, index) => {
                  const isOpen = openFaqIndex === index;
                  return (
                    <div 
                      key={index}
                      className={`faq-airy-item ${isOpen ? 'open' : ''}`}
                      onClick={() => setOpenFaqIndex(isOpen ? null : index)}
                    >
                      <div className="faq-airy-question-row">
                        <h3 className="faq-airy-question">{faq.q}</h3>
                        <div className="faq-airy-toggle-btn">
                          <svg 
                            className="faq-airy-icon" 
                            width="20" 
                            height="20" 
                            viewBox="0 0 24 24" 
                            fill="none" 
                            stroke="currentColor" 
                            strokeWidth="2.5"
                          >
                            <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                      <div className="faq-airy-answer-wrapper">
                        <div className="faq-airy-answer-inner">
                          <div className="faq-airy-answer-box">
                            <p className="faq-airy-answer">{faq.a}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>
          */}


        </div>
      </main>
      
      {/* Footer */}
      <LandingAppStoreFooter 
        subjects={allSubjects} 
        onSubjectClick={setSelectedSubject}
      />
    </div>
  );
};

export default LandingAppStore;
